import { html, LitElement, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { RefElement } from '../../../page-templates/base-page/base-page-header';
import '../ui-game-info/ui-favorite-icon';
import './ui-header-icon-back';
import './ui-header-icon-notification';
import './ui-header-icon-switcher';
import './ui-header-icon-user';
import styles from './ui-header.css?inline';

const CName = 'ui-header';

@customElement(CName)
export class UIHeader extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: String }) class = '';
  @property({ type: Object }) scrollableContainerRef: RefElement;
  @property({ type: Boolean }) isLoggedIn = false;

  private _opacity = '0';
  private readonly _scrollRange = 50; // The scroll position at which the background becomes fully filled
  private readonly _abortController = new AbortController();

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._abortController.abort();
  }

  protected firstUpdated() {
    let target: Window | Element = window;
    if (window.$app.appConfig.iOS26FixEnabled) {
      target = document.body;
    }

    if (this.scrollableContainerRef?.value) {
      target = this.scrollableContainerRef.value;
    }

    let hasUpdatedForFrame = false;

    const onScroll = () => {
      if (hasUpdatedForFrame) {
        // Compute only once per frame
        return;
      }
      hasUpdatedForFrame = true;

      requestAnimationFrame(() => {
        const scrollPosition = Math.max(this._getScrollY(target), 0);
        const normalizedScroll = Math.min(scrollPosition / this._scrollRange, 1);
        const newOpacity = (normalizedScroll ** 2).toFixed(1);
        // Only update when opacity changes
        if (newOpacity !== this._opacity) {
          this._opacity = newOpacity;
          this.style.setProperty('--header-opacity', this._opacity);
        }
        hasUpdatedForFrame = false;
      });
    };

    target.addEventListener('scroll', onScroll, { passive: true, signal: this._abortController.signal });
  }

  render() {
    return html`
      <header role="banner" class="header-wrapper ${this.class} ${this.isLoggedIn ? 'header-wrapper--logged-in' : ''}">
        <slot name="left"></slot>
        <slot name="center"></slot>
        <slot name="right"></slot>
        <slot name="top"></slot>
      </header>
    `;
  }

  private _getScrollY(element: Window | Element) {
    if (element === window) {
      return window.scrollY;
    }

    return (element as Element).scrollTop;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UIHeader;
  }
}
